import { activityPanelsSlot } from '@/features/catalog/activities/activity'
import { courseAdvancedFieldsSlot, courseFormSectionsSlot } from '@/features/catalog/courses/paths'
import { classSlots } from '@/features/classes/slots'
import { studentDashboardSlots } from '@/features/entitlements/my-training/paths'
import { transcriptRowActionsSlot, transcriptSlot } from '@/features/learner-history/transcripts/paths'
import { organizationFormSectionsSlot } from '@/features/site-management/organizations/paths'
import { userDetailsSlot } from '@/features/site-management/users/user'
import { detailsSlots } from '@/shared/details'
import { shellSlots, type Feature } from '@/shared/registry'
import { myDataSlot } from '@/shared/search/my-data'
import { Award, BadgeCheck, Trophy } from '@/shared/ui/icons'

import { achievementRoute, achievementsPath, createAchievementPath, editAchievementRoute, readAchievementsPermission } from './achievements/paths'
import { badgeRoute, badgesPath, createBadgePath, editBadgeRoute, readBadgesPermission, writeBadgesPermission } from './badges/paths'
import {
  certificateTemplateRoute,
  certificateTemplatesPath,
  createCertificateTemplatePath,
  editCertificateTemplateRoute,
  readCertificateTemplatesPermission,
} from './certificate-templates/paths'
import { readCourseCertificatesPermission, writeCourseCertificatesPermission } from './course-certificates/paths'
import {
  badgeProviderRoute,
  badgeProvidersPath,
  createBadgeProviderPath,
  editBadgeProviderRoute,
  readCredlyConnectionsPermission,
  writeCredlyConnectionsPermission,
} from './credly/paths'
import {
  issueBadgesPath,
  issueBadgesRoute,
  issuedBadgeRoute,
  issuedBadgesPath,
  readIssuedBadgesPermission,
  writeIssuedBadgesPermission,
} from './issued-badges/paths'
import { myAchievementsPath, personAchievementsPath, personAchievementsRoute, readEarnedCredentialsPermission } from './my-achievements/paths'
import { myDataApi } from './my-data/paths'
import { classCertificatesPath, classCertificatesRoute, printCertificatesPermission } from './print/class/class-print'
import { skillTabs } from './skills/skill-tabs'
import { SkillsProvider } from './skills/SkillsProvider'

/** Credentials: certificates, achievements, Credly badges and skill scores. Its screens register here. */
export const feature: Feature = {
  id: 'credentials',
  // Find, Create, Details and Edit Badge Provider: Credly connections (CRD-11).
  routes: [
    {
      path: badgeProvidersPath,
      titleId: 'credentials.badgeProviders.find.title',
      page: () => import('./credly/FindBadgeProvidersPage').then((module) => module.FindBadgeProvidersPage),
    },
    {
      path: createBadgeProviderPath,
      titleId: 'credentials.badgeProviders.create.title',
      page: () => import('./credly/BadgeProviderFormPages').then((module) => module.CreateBadgeProviderPage),
    },
    {
      path: badgeProviderRoute,
      titleId: 'credentials.badgeProviders.details.title',
      page: () => import('./credly/BadgeProviderDetailsPage').then((module) => module.BadgeProviderDetailsPage),
    },
    {
      path: editBadgeProviderRoute,
      titleId: 'credentials.badgeProviders.edit.title',
      page: () => import('./credly/BadgeProviderFormPages').then((module) => module.EditBadgeProviderPage),
    },
    // Find, Create, Details and Edit Badge (CRD-12).
    {
      path: badgesPath,
      titleId: 'credentials.badges.find.title',
      page: () => import('./badges/FindBadgesPage').then((module) => module.FindBadgesPage),
    },
    {
      path: createBadgePath,
      titleId: 'credentials.badges.create.title',
      page: () => import('./badges/BadgeFormPages').then((module) => module.CreateBadgePage),
    },
    {
      path: badgeRoute,
      titleId: 'credentials.badges.details.title',
      page: () => import('./badges/BadgeDetailsPage').then((module) => module.BadgeDetailsPage),
    },
    {
      path: editBadgeRoute,
      titleId: 'credentials.badges.edit.title',
      page: () => import('./badges/BadgeFormPages').then((module) => module.EditBadgePage),
    },
    // Find, Create, Details and Edit Achievement (CRD-08).
    {
      path: achievementsPath,
      titleId: 'credentials.achievements.find.title',
      page: () => import('./achievements/FindAchievementsPage').then((module) => module.FindAchievementsPage),
    },
    {
      path: createAchievementPath,
      titleId: 'credentials.achievements.create.title',
      page: () => import('./achievements/AchievementFormPages').then((module) => module.CreateAchievementPage),
    },
    {
      path: achievementRoute,
      titleId: 'credentials.achievements.details.title',
      page: () => import('./achievements/AchievementDetailsPage').then((module) => module.AchievementDetailsPage),
    },
    {
      path: editAchievementRoute,
      titleId: 'credentials.achievements.edit.title',
      page: () => import('./achievements/AchievementFormPages').then((module) => module.EditAchievementPage),
    },
    // Find Issued Badges, Issued Badge Details and Issue Badges for a class (CRD-13).
    {
      path: issuedBadgesPath,
      titleId: 'credentials.issuedBadges.find.title',
      page: () => import('./issued-badges/FindIssuedBadgesPage').then((module) => module.FindIssuedBadgesPage),
    },
    {
      path: issuedBadgeRoute,
      titleId: 'credentials.issuedBadges.details.title',
      page: () => import('./issued-badges/IssuedBadgeDetailsPage').then((module) => module.IssuedBadgeDetailsPage),
    },
    {
      path: issueBadgesRoute,
      titleId: 'credentials.issueBadges.title',
      page: () => import('./issued-badges/IssueBadgesPage').then((module) => module.IssueBadgesPage),
    },
    // Find, Create, Details and Edit Certificate Template, with its preview (CRD-03).
    {
      path: certificateTemplatesPath,
      titleId: 'credentials.certificateTemplates.find.title',
      page: () => import('./certificate-templates/FindCertificateTemplatesPage').then((module) => module.FindCertificateTemplatesPage),
    },
    {
      path: createCertificateTemplatePath,
      titleId: 'credentials.certificateTemplates.create.title',
      page: () => import('./certificate-templates/CertificateTemplateFormPages').then((module) => module.CreateCertificateTemplatePage),
    },
    {
      path: certificateTemplateRoute,
      titleId: 'credentials.certificateTemplates.details.title',
      page: () => import('./certificate-templates/CertificateTemplateDetailsPage').then((module) => module.CertificateTemplateDetailsPage),
    },
    {
      path: editCertificateTemplateRoute,
      titleId: 'credentials.certificateTemplates.edit.title',
      page: () => import('./certificate-templates/CertificateTemplateFormPages').then((module) => module.EditCertificateTemplatePage),
    },
    // A person's Achievements page: their own, and a person's for an administrator (CRD-10).
    {
      path: myAchievementsPath,
      titleId: 'credentials.myAchievements.title',
      page: () => import('./my-achievements/MyAchievementsPage').then((module) => module.MyAchievementsPage),
    },
    {
      path: personAchievementsRoute,
      titleId: 'credentials.myAchievements.title',
      page: () => import('./my-achievements/MyAchievementsPage').then((module) => module.MyAchievementsPage),
    },
    // Completion Certificates for a class: every student's certificate in one zip (CRD-07).
    {
      path: classCertificatesRoute,
      titleId: 'credentials.classPrint.title',
      page: () => import('./print/class/ClassCertificatesPage').then((module) => module.ClassCertificatesPage),
    },
  ],
  adminHome: {
    links: [
      {
        id: 'find-badge-providers',
        category: 'badges',
        labelId: 'credentials.badgeProviders.find.title',
        path: badgeProvidersPath,
        permission: readCredlyConnectionsPermission,
      },
      {
        id: 'create-badge-provider',
        category: 'badges',
        labelId: 'credentials.badgeProviders.create.title',
        path: createBadgeProviderPath,
        permission: writeCredlyConnectionsPermission,
      },
      {
        id: 'find-badges',
        category: 'badges',
        labelId: 'credentials.badges.find.title',
        path: badgesPath,
        permission: readBadgesPermission,
      },
      {
        id: 'create-badge',
        category: 'badges',
        labelId: 'credentials.badges.create.title',
        path: createBadgePath,
        permission: writeBadgesPermission,
      },
      {
        id: 'find-issued-badges',
        category: 'badges',
        labelId: 'credentials.issuedBadges.find.title',
        path: issuedBadgesPath,
        permission: readIssuedBadgesPermission,
      },
      {
        // The mockups' Admin Home lists "Manage Completion Certificates" under Courses.
        id: 'manage-completion-certificates',
        category: 'courses',
        labelId: 'credentials.certificateTemplates.adminLink',
        path: certificateTemplatesPath,
        permission: readCertificateTemplatesPermission,
      },
      {
        // The mockups' Admin Home lists Achievements under Organizations.
        id: 'achievements',
        category: 'organizations',
        labelId: 'credentials.achievements.adminLink',
        path: achievementsPath,
        permission: readAchievementsPermission,
      },
    ],
  },
  slots: [
    // Credentials' part of "Download my data" on My Profile (CRD-20).
    { kind: 'link', slot: myDataSlot, id: 'credentials.my-data', labelId: 'credentials.myData.part', path: myDataApi },
    {
      // The `credentials` settings group on Create and Edit Organization (CRD-16).
      kind: 'tab',
      slot: organizationFormSectionsSlot,
      id: 'credentials',
      labelId: 'credentials.settings.label',
      component: () =>
        import('./organization-settings/CredentialsSettingsSection').then((module) => module.CredentialsSettingsSection),
    },
    {
      // "Enable achievements for outside organizations" at the end of Advanced on Create and Edit Course (CRD-17).
      kind: 'panel',
      slot: courseAdvancedFieldsSlot,
      id: 'course-achievements',
      component: () =>
        import('./course-achievements/CourseAchievementSettingField').then((module) => module.CourseAchievementSettingField),
    },
    {
      // The Completion Certificates tab on Create and Edit Course: the templates the course gives (CRD-04).
      kind: 'tab',
      slot: courseFormSectionsSlot,
      id: 'certificates',
      labelId: 'credentials.courseCertificates.label',
      permission: [readCourseCertificatesPermission, writeCourseCertificatesPermission],
      component: () => import('./course-certificates/CourseCertificatesTab').then((module) => module.CourseCertificatesTab),
    },
    // The Skills tabs on Course, Class and Enrollment Details, shown only while skills show to the viewer (CRD-14).
    ...skillTabs,
    {
      // A lab activity's skills, on each Activities tab row and on Activity Details (CRD-14).
      kind: 'panel',
      slot: activityPanelsSlot,
      id: 'skills',
      component: () => import('./skills/ActivitySkills').then((module) => module.ActivitySkills),
    },
    {
      // The Skills section on a transcript: the person's skill scores, while skills show to the viewer (CRD-15).
      kind: 'panel',
      slot: detailsSlots(transcriptSlot).panels,
      id: 'skill-scores',
      component: () => import('./skills/TranscriptSkillsSection').then((module) => module.TranscriptSkillsSection),
    },
    {
      // The trophy link to the person's own achievements, in the nav bar, while it shows (CRD-10).
      kind: 'action',
      slot: shellSlots.headerActions,
      id: 'trophy',
      component: () => import('./my-achievements/TrophyLink').then((module) => module.TrophyLink),
    },
    {
      // The Rewards card on the Student Dashboard (CRD-10).
      kind: 'panel',
      slot: studentDashboardSlots.side,
      id: 'rewards',
      component: () => import('./my-achievements/RewardsCard').then((module) => module.RewardsCard),
    },
    {
      // A person's achievements, from User Details' side menu (CRD-10).
      kind: 'link',
      slot: detailsSlots(userDetailsSlot).menu,
      id: 'achievements',
      labelId: 'credentials.myAchievements.title',
      permission: readEarnedCredentialsPermission,
      isPersonRecord: true,
      icon: Trophy,
      path: (context) => personAchievementsPath(context.recordId ?? ''),
    },
    {
      // Completion Certificates in Class Details' sidebar, before Issue Badges as in the mockups' "Files, Certificates & Badges" group (CRD-07).
      kind: 'link',
      slot: classSlots.detailsSidebar,
      id: 'completion-certificates',
      labelId: 'credentials.classPrint.title',
      icon: Award,
      permission: printCertificatesPermission,
      path: (context) => classCertificatesPath(context.recordId ?? ''),
    },
    {
      // Issue Badges in Class Details' sidebar, in the mockups' "Files, Certificates & Badges" group (CRD-13).
      kind: 'link',
      slot: classSlots.detailsSidebar,
      id: 'issue-badges',
      labelId: 'credentials.issueBadges.title',
      icon: BadgeCheck,
      permission: writeIssuedBadgesPermission,
      path: (context) => issueBadgesPath(context.recordId ?? ''),
    },
    {
      // The instructor dashboard's Achievements quick link: the instructor's own (CRD-10).
      kind: 'link',
      slot: classSlots.dashboardLinks,
      id: 'achievements',
      labelId: 'credentials.myAchievements.title',
      path: myAchievementsPath,
    },
    {
      // Completion Certificates on Create and Edit Class: the class's own templates and whether it gives its course's (CRD-05).
      kind: 'panel',
      slot: classSlots.formSections,
      id: 'class-certificates',
      component: () => import('./class-certificates/ClassCertificatesSection').then((module) => module.ClassCertificatesSection),
    },
    {
      // The Certificate button on a transcript's completed rows, My Transcript's and a person's (CRD-06).
      kind: 'action',
      slot: transcriptRowActionsSlot,
      id: 'certificate',
      component: () => import('./print/CertificateButton').then((module) => module.CertificateButton),
    },
  ],
  // Leaves the Skills tabs out while skills do not show to the viewer (CRD-14).
  provider: SkillsProvider,
}
